import { Check, ChevronDown } from 'lucide-react';
import { useCallback, useId, useRef, useState, type ReactNode } from 'react';
import { cn } from './cn.js';
import { FloatingPanel } from './floating-panel.js';

export interface SelectOption {
  value: string;
  label: string;
  /// Linha secundária, usada pela lista com busca — telefone e documento do cliente, por exemplo.
  description?: string | undefined;
}

export function Select({
  label,
  value,
  options,
  onValueChange,
  className,
  disabled,
  error,
  labelAddon,
}: {
  label: string;
  /// Elemento ao lado do rótulo, como o ícone de ajuda (`HelpTip`).
  labelAddon?: ReactNode;
  value: string;
  options: SelectOption[];
  onValueChange: (value: string) => void;
  className?: string | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
}) {
  const [open, setOpen] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const id = useId();
  const selected = options.find((option) => option.value === value);

  return (
    <div className={cn('min-w-0 space-y-2', className)}>
      {labelAddon ? (
        <span className="flex items-center gap-1">
          <span id={`${id}-label`} className="block text-xs font-semibold text-content/70">
            {label}
          </span>
          {labelAddon}
        </span>
      ) : (
        <span id={`${id}-label`} className="block text-xs font-semibold text-content/70">
          {label}
        </span>
      )}
      <div className="relative min-w-0" ref={fieldRef}>
        <button
          type="button"
          disabled={disabled}
          aria-labelledby={`${id}-label ${id}-value`}
          aria-controls={`${id}-options`}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          onClick={() => setOpen((current) => !current)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setOpen(false);
          }}
          className={cn(
            'hue-select flex min-h-12 w-full min-w-0 items-center gap-2 rounded-xl border bg-surface-elevated px-4 text-left transition hover:border-brand-500/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:cursor-not-allowed disabled:opacity-50',
            error ? 'border-danger/70' : 'border-surface-border',
          )}
        >
          <span id={`${id}-value`} className="min-w-0 flex-1 truncate text-sm">
            {selected?.label ?? 'Selecionar'}
          </span>
          <ChevronDown className={cn('size-4 shrink-0 transition', open && 'rotate-180')} />
        </button>
        {open ? (
          <FloatingPanel
            anchorRef={fieldRef}
            onDismiss={close}
            width="anchor"
            id={`${id}-options`}
            role="listbox"
            aria-label={label}
            className="max-h-64 rounded-2xl border border-surface-border bg-surface-card p-2 shadow-card"
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                onClick={() => {
                  onValueChange(option.value);
                  setOpen(false);
                }}
                className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
              >
                <span className="min-w-0 flex-1 truncate text-xs font-medium">{option.label}</span>
                {option.value === value ? (
                  <Check className="size-4 shrink-0 text-brand-400" />
                ) : null}
              </button>
            ))}
          </FloatingPanel>
        ) : null}
      </div>
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
